<template>
  <div
    v-if="open"
    class="fixed inset-0 z-40 flex items-center justify-center bg-black/50"
    @click.self="close"
  >
    <div
      class="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-white dark:bg-gray-800 shadow-xl p-6 space-y-4"
      role="dialog"
      aria-modal="true"
      @click.stop
    >
      <header class="flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Buy {{ credits.toLocaleString() }} credits (${{ (amountCents / 100).toFixed(0) }})
        </h3>
        <button
          type="button"
          class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          @click="close"
        >
          ✕
        </button>
      </header>

      <section v-if="!needsCard" class="text-sm text-gray-700 dark:text-gray-300">
        <p>
          Confirm prepaid credit purchase. Credits will be added immediately.
        </p>
      </section>

      <section v-else class="space-y-3">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Enter your card details to complete the purchase.
        </p>

        <TwoCheckoutCardForm ref="cardForm" :dark="dark" :lang="lang" />

        <div class="grid grid-cols-2 gap-2 pt-2">
          <label class="text-xs text-gray-600 dark:text-gray-400 col-span-2">
            Billing address
          </label>
          <input
            v-model="billing.address1"
            type="text"
            placeholder="Address"
            class="col-span-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
          />
          <input
            v-model="billing.city"
            type="text"
            placeholder="City"
            class="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
          />
          <input
            v-model="billing.zip"
            type="text"
            placeholder="ZIP / postal"
            class="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
          />
          <input
            v-model="billing.state"
            type="text"
            placeholder="State / region"
            class="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
          />
          <input
            v-model="billing.country_code"
            type="text"
            placeholder="Country (ISO 2)"
            maxlength="2"
            class="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm uppercase"
          />
        </div>
      </section>

      <div v-if="error" class="text-sm text-red-600 dark:text-red-400">{{ error }}</div>

      <footer class="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" :disabled="submitting" @click="close">
          Cancel
        </Button>
        <Button variant="primary" size="sm" :loading="submitting" @click="handleSubmit">
          {{ submitting ? 'Processing…' : `Pay $${(amountCents / 100).toFixed(0)}` }}
        </Button>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { Button } from '@asabdev/asab-ui';
  import TwoCheckoutCardForm from './TwoCheckoutCardForm.vue';
  import billingApi from '../api/billing';
  import { isTestMode } from '../composables/useTestMode';
  import type { BillingProvider } from '@/features/organizations/types';
  import type { BillingAddressInput, TopUpResponse } from '../types';

  interface Props {
    open: boolean;
    amountCents: number;
    credits: number;
    provider?: BillingProvider;
    dark?: boolean;
    lang?: string;
  }
  const props = withDefaults(defineProps<Props>(), {
    provider: 'manual',
    dark: false,
    lang: 'en',
  });
  const emit = defineEmits<{
    (e: 'update:open', v: boolean): void;
    (e: 'success', payload: TopUpResponse): void;
  }>();

  const cardForm = ref<InstanceType<typeof TwoCheckoutCardForm> | null>(null);
  const billing = ref<BillingAddressInput>({
    address1: '',
    city: '',
    zip: '',
    state: '',
    country_code: '',
  });
  const submitting = ref(false);
  const error = ref('');

  const needsCard = computed(() => props.provider === '2checkout');

  function close() {
    if (submitting.value) return;
    emit('update:open', false);
  }

  async function handleSubmit() {
    error.value = '';
    submitting.value = true;
    try {
      let token = '';
      if (needsCard.value) {
        if (!cardForm.value) throw new Error('Card form not ready');
        const res = await cardForm.value.submit();
        token = res.token;
      }
      const resp = await billingApi.topUp({
        amount_cents: props.amountCents,
        credits: props.credits,
        ees_token: token || undefined,
        test_mode: isTestMode() || undefined,
        billing_details: {
          ...billing.value,
          country_code: (billing.value.country_code ?? '').toUpperCase() || undefined,
        },
      });
      emit('success', resp);
      emit('update:open', false);
    } catch (e: unknown) {
      const apiErr = (e as { response?: { data?: { error?: { message?: string; code?: string } } } })
        ?.response?.data?.error;
      if (apiErr?.code === 'INVALID_EES_TOKEN') {
        error.value = 'Card declined or token expired. Please re-enter your card details.';
      } else if (apiErr?.message) {
        error.value = apiErr.message;
      } else {
        error.value = (e as Error).message ?? 'Top-up failed';
      }
    } finally {
      submitting.value = false;
    }
  }

  watch(
    () => props.open,
    (v) => {
      if (v) {
        error.value = '';
        submitting.value = false;
      }
    },
  );
</script>
